import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { addYears } from 'date-fns'
import { formatDate, useEffectOnce, usePathParam } from '@chilecompra/react-kit'
import { CircleWarningIcon, Skeleton, Tag, Toast, Typography } from '@chilecompra/react-kit/components'

import { PUBLIC_URL, REACT_APP_FEATURE_FLAG_USER_INFO } from '../../config/settings/environment'
import { ABILITY_STATUS_TYPES, OFFSET_LIMIT_MEMBERSHIP, USER_TYPE } from '../../config/settings/constant'
import { checkIfForeignDni } from '../../modules/utils/helpers'
import { useAuthorization } from '../AuthorizationProvider/AuthorizationProvider.hooks'
import {
  onGetBasicSellerInformationThunk,
  onGetEconomicActivitiesInfoThunk,
  onGetSellerBlockedInfoThunk,
  onGetSellerLegalAddressThunk,
  onGetUserPersonalInfoThunk
} from './BasicSellerInformationSection.actions'
import { onGetFinancialSuitabilityThunk } from '../FinancialSuitabilitySection/FinancialSuitabilitySection.actions'
import EMTModal from './BasicSellerInformation.EMTModal'
import TableSellerInfo from './BasicSellerInformation.tableSellerInfo'
import { Box, Card, Column, Logo, NotFoundImg, Strong } from './BasicSellerInformationSection.styles'

/**
 * The seller general information section container.
 */
const SellerGeneralInformation = props => {
  const { options } = props

  const dispatch = useDispatch()
  const dni = usePathParam('rut')
  const { isPrivated, userType } = useAuthorization()

  const isForeign = checkIfForeignDni(dni)

  const [unlimitedMembership, setUnlimitedMembership] = useState(false)
  const [openEMTModal, setOpenEMTModal] = useState(false)

  const {
    EMTClassificationDetail,
    abilityStatus,
    accreditationEndDate,
    blockedInfo,
    contractStatus,
    error,
    suspensionReason,
    isMinorSizeSeller,
    loading,
    orgAddress,
    orgLegalName,
    orgName,
    sellerIsEliminated,
    sellerIsSuspended,
    userEconomicActivities,
    userInfo
  } = useSelector(state => state.basicSellerInformation)

  const today = new Date()
  const formatAccreditationEndDate = accreditationEndDate && formatDate(accreditationEndDate, 'dd-MM-yyyy')

  useEffect(() => {
    setUnlimitedMembership(addYears(new Date(accreditationEndDate), -OFFSET_LIMIT_MEMBERSHIP) > today)
  }, [accreditationEndDate])

  useEffectOnce(() => {
    if (!loading) return

    if (REACT_APP_FEATURE_FLAG_USER_INFO === true || REACT_APP_FEATURE_FLAG_USER_INFO === 'true') {
      dispatch(onGetUserPersonalInfoThunk({ dni }))
    }

    dispatch(onGetEconomicActivitiesInfoThunk({ dni }))
    dispatch(onGetSellerLegalAddressThunk({ dni }))
    dispatch(onGetFinancialSuitabilityThunk({ dni, isPrivated }))
  })

  useEffectOnce(() => {
    if (!loading || userType === '') return

    dispatch(onGetBasicSellerInformationThunk({ dni, userType }))
    dispatch(onGetSellerBlockedInfoThunk({ dni }))
  }, [userType])

  const toggleEMTModal = useCallback(() => setOpenEMTModal(prev => !prev), [])

  return (
    <>
      {!loading && error && error.reason === 'DNI_DOES_NOT_MATCH_ERROR' && (
        <Column margin="14px 0 2rem" padding="0 27px 0 0">
          <Card padding="75.5px 0 74.5px" textAlign="center">
            <NotFoundImg src={`${PUBLIC_URL}/assets/images/notFound.svg`} />

            <Typography variant="h4" fontWeight="bold" margin="8px 0">
              No se encontraron resultados para tu búsqueda
            </Typography>

            <Typography variant="body1" color="black3" fontSize="14px" lineHeight="18px">
              El proveedor que buscas no se encuentra inscrito en
              <br />
              Mercado Público. Intenta con otra palabra clave u
              <br />
              otro criterio de búsqueda.
            </Typography>
          </Card>
        </Column>
      )}

      {!loading && error && error.reason === 'TIME_OUT_ERROR' && (
        <Column margin="16px 0 32px 0" padding="0">
          <Card textAlign="center" padding="40px 20px">
            <CircleWarningIcon>Icon</CircleWarningIcon>

            <Typography color="black3" variant="body2" margin="16px 0 0">
              No ha sido posible obtener información de esta empresa proveedora.
              <br />
              Por favor, te pedimos que lo vuelvas a intentar dentro de unos minutos.
            </Typography>
          </Card>
        </Column>
      )}

      {!loading && error && error.reason === 'SOMETHING_WENT_WRONG_ERROR' && (
        <Column margin="14px 0 2rem" padding="0 27px 0 0">
          <Toast
            border
            severity="error"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor"
          >
            En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en unos minutos
            más.
          </Toast>
        </Column>
      )}

      {!loading && error && error.reason === 'UNAUTHORIZED_ERROR' && (
        <Column margin="14px 0 2rem" padding="0 27px 0 0">
          <Toast
            border
            severity="error"
            textDirection="vertical"
            title="Lo sentimos, estamos presentando problemas con nuestro servidor"
          >
            No esta autenticado.
          </Toast>
        </Column>
      )}

      {loading && (
        <Column margin="14px 0 2rem" padding="0 27px 0 0">
          <Skeleton height={isPrivated ? '356px' : '167px'} variant="rect" width="100%" />
        </Column>
      )}

      {!error && !loading && (
        <Column margin="14px 0 2rem" padding="0 27px 0 0">
          <Card color="default" padding="32px">
            <Box display="flex" flexDirection="column" gap="12px">
              <Box display="flex" gap="8px" justifyContent="flex-end">
                {isForeign && <Tag color="info" text="Proveedor extranjero" />}

                {ABILITY_STATUS_TYPES[abilityStatus] && (
                  <Tag
                    color={ABILITY_STATUS_TYPES[abilityStatus].color}
                    enableIcon={ABILITY_STATUS_TYPES[abilityStatus].withIcon || false}
                    text={ABILITY_STATUS_TYPES[abilityStatus].label}
                  />
                )}
              </Box>

              <Box display="flex" gap="16px">
                <Logo src={`${PUBLIC_URL}/assets/images/defaultLogo.svg`} />

                <Box>
                  <Typography color="black2" fontWeight="bold" variant="caption">
                    Razón social
                  </Typography>

                  <Typography fontWeight="bold" variant="h3">
                    {orgLegalName}
                  </Typography>

                  <Typography color="black3" variant="h3">
                    {`${isForeign ? 'ID' : 'RUT'} ${dni}`}
                  </Typography>
                </Box>
              </Box>

              {!blockedInfo.isBlocked && !loading && !error && (sellerIsSuspended || sellerIsEliminated) && (
                <Toast severity="error">
                  {sellerIsEliminated ? (
                    'Proveedor eliminado del Registro de Proveedores.'
                  ) : (
                    <>
                      El proveedor se encuentra actualmente suspendido en el Registro de Proveedores por el siguiente
                      motivo: <Strong>{suspensionReason}</Strong>
                    </>
                  )}
                </Toast>
              )}

              {!blockedInfo.loading && (
                <>
                  {blockedInfo.error && (
                    <Toast
                      severity="error"
                      textDirection="vertical"
                      title="Lo sentimos, estamos presentando problemas con nuestro servidor"
                    >
                      En estos momentos tenemos problemas para disponibilizar la información. Por favor, consulta en
                      unos minutos más.
                    </Toast>
                  )}

                  {!blockedInfo.error && blockedInfo.isBlocked && (
                    <Toast severity="error" title="Proveedor bloqueado en Mercado Público">
                      La empresa se encuentra bloqueada debido a: {blockedInfo.reason}.
                    </Toast>
                  )}
                </>
              )}

              <TableSellerInfo
                EMTClassificationDetail={EMTClassificationDetail}
                abilityStatus={abilityStatus}
                accreditationEndDate={formatAccreditationEndDate}
                contractStatus={contractStatus}
                error={error}
                isForeign={isForeign}
                isMinorSizeSeller={isMinorSizeSeller}
                loading={loading}
                options={options}
                orgAddress={orgAddress}
                orgName={orgName}
                toggleEMTModal={toggleEMTModal}
                unlimitedMembership={unlimitedMembership}
              />
            </Box>
          </Card>

          {userType !== USER_TYPE.PUBLIC && userInfo?.dateOfDeathPresumed > '0000-00-0000' && (
            <Toast severity="warning" margin="20px 0px 0px" title="Persona fallecida:">
              Según la información del Registro Civil, esta persona se encuentra fallecida.
            </Toast>
          )}

          {userEconomicActivities?.activities?.economicActivity === null && (
            <Toast
              severity="warning"
              textDirection="vertical"
              margin="20px 0px 0px"
              title="Proveedor sin inicio de actividades:"
            >
              Según la información obtenida desde el Servicio de Impuestos Internos, este proveedor no cuenta con inicio
              de actividades.
            </Toast>
          )}
        </Column>
      )}

      <EMTModal EMTClassificationDetail={EMTClassificationDetail} isOpen={openEMTModal} onClose={toggleEMTModal} />
    </>
  )
}

SellerGeneralInformation.propTypes = {
  options: PropTypes.arrayOf(
    PropTypes.shape({
      content: PropTypes.node.isRequired,
      current: PropTypes.bool.isRequired,
      label: PropTypes.string.isRequired,
      route: PropTypes.string.isRequired
    })
  ).isRequired
}

export default SellerGeneralInformation
